import {
  ETIQUETA_CATEGORIA_DE_CUMPLIMIENTO,
  ETIQUETA_CATEGORIA_DE_CUMPLIMIENTO_EN,
  type ResultadoDeRevisionDeCarpeta,
} from '@cnc/contracts';
import {useQueryClient} from '@tanstack/react-query';
import {useRef, useState} from 'react';
import {
  revisarCarpeta,
  rutaDentroDeLaCarpeta,
  subirArchivoDeCarpeta,
  terminarCargaDeCarpeta,
} from '../api/cumplimiento';
import {et, tr, type Texto} from '../i18n/idioma';
import {numero} from '../utilidades/formato';
import {AYUDA_CARGA_CARPETA as AC} from './ayuda-cumplimiento';
import {NotaDeAyuda} from './nota-de-ayuda';
import {PistaDeSoltar, ZonaParaSoltar} from './zona-para-soltar';

// Archivos que se suben a la vez: suficiente para ir rapido sin ahogar al servidor.
const A_LA_VEZ = 3;

type Avance = {
  total: number;
  hechos: number;
  cargados: number;
  yaEstaban: number;
  ignorados: number;
  errores: string[];
};

// Cumplimiento → Cargar carpeta (decision 0045): se elige la carpeta de
// compliance completa; primero se revisa sin guardar nada y despues se suben
// los archivos nuevos o cambiados, uno por uno, con su ruta.
export function CargaDeCarpeta({onTerminar}: {onTerminar: () => void}) {
  const cliente = useQueryClient();
  const entrada = useRef<HTMLInputElement>(null);
  const [archivos, setArchivos] = useState<File[]>([]);
  const [carpeta, setCarpeta] = useState('');
  const [revision, setRevision] = useState<ResultadoDeRevisionDeCarpeta | null>(null);
  const [revisando, setRevisando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [avance, setAvance] = useState<Avance | null>(null);
  const [subiendo, setSubiendo] = useState(false);
  const categorias = et(ETIQUETA_CATEGORIA_DE_CUMPLIMIENTO, ETIQUETA_CATEGORIA_DE_CUMPLIMIENTO_EN);

  const elegir = async (lista: File[]) => {
    setArchivos(lista);
    setRevision(null);
    setAvance(null);
    setError(null);
    if (!lista.length) return;
    setCarpeta((lista[0]!.webkitRelativePath || '').split('/')[0] ?? '');
    setRevisando(true);
    try {
      setRevision(await revisarCarpeta(lista));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRevisando(false);
    }
  };

  const cargar = async () => {
    if (!revision) return;
    const porSubir = new Set(revision.porSubir);
    const cola = archivos.filter(a => porSubir.has(rutaDentroDeLaCarpeta(a)));
    const estado: Avance = {total: cola.length, hechos: 0, cargados: 0, yaEstaban: 0, ignorados: 0, errores: []};
    setAvance({...estado});
    setSubiendo(true);
    let siguiente = 0;
    const trabajador = async () => {
      while (siguiente < cola.length) {
        const archivo = cola[siguiente++]!;
        try {
          const r = await subirArchivoDeCarpeta(archivo);
          if (r.resultado === 'cargado') estado.cargados++;
          else if (r.resultado === 'ya_estaba') estado.yaEstaban++;
          else estado.ignorados++;
        } catch (e) {
          estado.errores.push(`${rutaDentroDeLaCarpeta(archivo)}: ${e instanceof Error ? e.message : String(e)}`);
        }
        estado.hechos++;
        setAvance({...estado, errores: [...estado.errores]});
      }
    };
    await Promise.all(Array.from({length: A_LA_VEZ}, trabajador));
    await terminarCargaDeCarpeta({
      cargados: estado.cargados,
      yaEstaban: estado.yaEstaban,
      ignorados: estado.ignorados,
      errores: estado.errores.length,
    }).catch(() => undefined);
    setSubiendo(false);
    void cliente.invalidateQueries({queryKey: ['cumplimiento']});
  };

  const porcentaje = avance && avance.total ? Math.round((avance.hechos / avance.total) * 100) : 0;
  return (
    <div className="space-y-4" data-prueba="cumplimiento-carga">
      <div className="rounded-lg border border-linea bg-white p-4">
        <h2 className="font-medium">{tr('Cargar la carpeta de compliance', 'Load the compliance folder')}</h2>
        <p className="mt-1 max-w-3xl text-sm text-apagado">
          {tr(
            'Elige la carpeta Compliance completa (la de Dropbox). Primero se revisa sin guardar nada: cuántos archivos hay, a qué vínculo y categoría va cada uno y qué tipo de documento se reconoció. Después se suben solo los nuevos o cambiados; lo que ya está no se repite. Los archivos quedan en el servidor de la app, nunca en GitHub, y la carpeta de Dropbox no se modifica.',
            'Choose the whole Compliance folder (the Dropbox one). It is reviewed first without saving anything: how many files there are, which link and category each one goes to and which document type was recognized. Then only new or changed files are uploaded; what is already there is not repeated. Files stay on the app server, never on GitHub, and the Dropbox folder is not changed.',
          )}
        </p>
        <ZonaParaSoltar
          carpeta
          deshabilitado={subiendo || revisando}
          onSoltar={lista => void elegir(lista)}
          className="mt-3 flex flex-wrap items-center gap-3"
        >
          <label
            className={`inline-flex cursor-pointer items-center gap-1 rounded-md border border-verde bg-white px-3 py-1.5 text-sm text-verde-oscuro hover:bg-verde-claro ${subiendo || revisando ? 'pointer-events-none opacity-50' : ''}`}
          >
            📂 {tr('Elegir carpeta', 'Choose folder')}
            <input
              ref={entrada}
              type="file"
              multiple
              aria-label={tr('Carpeta de compliance', 'Compliance folder')}
              className="sr-only"
              {...{webkitdirectory: '', directory: ''}}
              onChange={e => {
                void elegir([...(e.target.files ?? [])]);
                e.target.value = '';
              }}
            />
          </label>
          {carpeta && (
            <span className="text-sm text-apagado">
              {tr(`${carpeta}: ${numero(archivos.length)} archivos`, `${carpeta}: ${numero(archivos.length)} files`)}
            </span>
          )}
          {!carpeta && !revisando && <PistaDeSoltar carpeta />}
          {revisando && <span className="text-sm text-apagado">{tr('Revisando…', 'Reviewing…')}</span>}
        </ZonaParaSoltar>
        {error && <p className="mt-2 text-sm text-alerta">{error}</p>}
      </div>

      {revision && (
        <div className="space-y-3 rounded-lg border border-linea bg-white p-4" data-prueba="revision-de-carpeta">
          <h3 className="text-sm font-semibold tracking-wide text-apagado uppercase">
            {tr('Lo que se cargaría', 'What would be loaded')}
          </h3>
          <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2 lg:grid-cols-4">
            <Cifra
              titulo={tr('Archivos en la carpeta', 'Files in the folder')}
              valor={revision.archivos}
              ayuda={AC.archivos}
            />
            <Cifra
              titulo={tr('Nuevos o cambiados', 'New or changed')}
              valor={revision.nuevos}
              fuerte
              ayuda={AC.nuevos}
            />
            <Cifra
              titulo={tr('Ya estaban en la app', 'Already in the app')}
              valor={revision.yaCargados}
              ayuda={AC.yaCargados}
            />
            <Cifra titulo={tr('Se ignoran', 'Ignored')} valor={revision.ignorados.length} ayuda={AC.ignorados} />
            <Cifra
              titulo={tr('Tipo reconocido', 'Type recognized')}
              valor={revision.clasificados}
              ayuda={AC.clasificados}
            />
            <Cifra
              titulo={tr('Sin clasificar', 'Unclassified')}
              valor={revision.sinClasificar}
              ayuda={AC.sinClasificar}
            />
            <Cifra
              titulo={tr('Formatos y plantillas', 'Forms and templates')}
              valor={revision.formatos}
              ayuda={AC.formatos}
            />
            <Cifra
              titulo={tr('Vencidos / por vencer', 'Expired / expiring')}
              valor={`${numero(revision.vencidos)} / ${numero(revision.porVencer)}`}
              ayuda={AC.vencimientos}
            />
          </dl>
          <div className="flex flex-wrap gap-2 text-xs">
            {revision.porCategoria.map(c => (
              <span key={c.categoria} className="rounded-full border border-linea px-2 py-0.5">
                {categorias[c.categoria]}:{' '}
                {tr(
                  `${numero(c.vinculos)} vínculos, ${numero(c.archivos)} archivos`,
                  `${numero(c.vinculos)} links, ${numero(c.archivos)} files`,
                )}
              </span>
            ))}
          </div>
          <details>
            <summary className="cursor-pointer text-sm text-verde">
              {tr(
                `Ver los ${numero(revision.vinculos.length)} vínculos`,
                `See the ${numero(revision.vinculos.length)} links`,
              )}
            </summary>
            <table className="mt-2 w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-apagado">
                  <th className="py-1 pr-2 font-semibold">
                    {tr('Categoría', 'Category')}
                    <NotaDeAyuda titulo={tr('Categoría', 'Category')} texto={AC.categoria} />
                  </th>
                  <th className="py-1 pr-2 font-semibold">
                    {tr('Vínculo', 'Link')}
                    <NotaDeAyuda titulo={tr('Vínculo', 'Link')} texto={AC.vinculo} />
                  </th>
                  <th className="py-1 pr-2 text-right font-semibold">
                    {tr('Archivos', 'Files')}
                    <NotaDeAyuda titulo={tr('Archivos', 'Files')} texto={AC.archivosVinculo} />
                  </th>
                  <th className="py-1 font-semibold">
                    {tr('Queda', 'Will be')}
                    <NotaDeAyuda titulo={tr('Queda', 'Will be')} texto={AC.queda} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {revision.vinculos.map(v => (
                  <tr key={`${v.categoria}-${v.nombre}`} className="border-t border-linea/70">
                    <td className="py-1 pr-2 text-xs">{categorias[v.categoria]}</td>
                    <td className="py-1 pr-2">
                      {v.nombre}
                      {v.nuevo && <span className="ml-1 text-xs text-verde">({tr('nuevo', 'new')})</span>}
                    </td>
                    <td className="py-1 pr-2 text-right tabular-nums">{numero(v.archivos)}</td>
                    <td className="py-1 text-xs">
                      {v.activo
                        ? tr('Activo (avisa)', 'Active (warns)')
                        : tr('Inactivo: sin archivos del último año', 'Inactive: no files from the last year')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
          {revision.ignorados.length > 0 && (
            <details>
              <summary className="cursor-pointer text-sm text-verde">
                {tr(
                  `Ver los ${numero(revision.ignorados.length)} ignorados`,
                  `See the ${numero(revision.ignorados.length)} ignored`,
                )}
              </summary>
              <ul className="mt-1 space-y-0.5 text-xs">
                {revision.ignorados.map(i => (
                  <li key={i.ruta} className="break-all">
                    {i.ruta} — <span className="text-apagado">{i.motivo}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
          {!avance && (
            <button
              type="button"
              disabled={!revision.nuevos}
              onClick={() => void cargar()}
              className="rounded-md bg-verde px-4 py-2 text-sm text-white hover:bg-verde-oscuro disabled:opacity-50"
            >
              {revision.nuevos
                ? tr(`⇪ Cargar ${numero(revision.nuevos)} archivos`, `⇪ Load ${numero(revision.nuevos)} files`)
                : tr('No hay nada nuevo para cargar', 'Nothing new to load')}
            </button>
          )}
        </div>
      )}

      {avance && (
        <div
          className="space-y-2 rounded-lg border border-linea bg-white p-4"
          role="status"
          data-prueba="avance-de-carga"
        >
          <div className="flex items-baseline justify-between text-sm">
            <span className="font-medium">
              {subiendo ? tr('Cargando…', 'Loading…') : tr('Carga terminada. ✓', 'Load finished. ✓')}
            </span>
            <span className="tabular-nums text-apagado">
              {numero(avance.hechos)} / {numero(avance.total)} ({porcentaje} %)
            </span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-papel">
            <div className="h-full bg-verde transition-all" style={{width: `${porcentaje}%`}} />
          </div>
          <p className="text-xs text-apagado">
            {tr(
              `Cargados: ${numero(avance.cargados)} · ya estaban: ${numero(avance.yaEstaban)} · ignorados: ${numero(avance.ignorados)} · con error: ${numero(avance.errores.length)}`,
              `Loaded: ${numero(avance.cargados)} · already there: ${numero(avance.yaEstaban)} · ignored: ${numero(avance.ignorados)} · with errors: ${numero(avance.errores.length)}`,
            )}
          </p>
          {subiendo && (
            <p className="text-xs text-apagado">
              {tr(
                'No cierres esta pestaña hasta que termine. Si se interrumpe, vuelve a elegir la carpeta: lo cargado no se repite.',
                'Do not close this tab until it finishes. If interrupted, choose the folder again: what was loaded is not repeated.',
              )}
            </p>
          )}
          {avance.errores.length > 0 && (
            <ul className="space-y-0.5 text-xs text-alerta">
              {avance.errores.slice(0, 20).map(e => (
                <li key={e} className="break-all">
                  {e}
                </li>
              ))}
            </ul>
          )}
          {!subiendo && (
            <button
              type="button"
              onClick={onTerminar}
              className="rounded-md border border-verde bg-white px-3 py-1.5 text-sm text-verde-oscuro hover:bg-verde-claro"
            >
              {tr('Ver el resumen', 'See the summary')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Cifra({
  titulo,
  valor,
  fuerte,
  ayuda,
}: {
  titulo: string;
  valor: number | string;
  fuerte?: boolean;
  ayuda?: Texto;
}) {
  return (
    <div className="flex justify-between gap-2 border-b border-linea/50 py-0.5">
      <dt className="text-apagado">
        {titulo}
        {ayuda && <NotaDeAyuda titulo={titulo} texto={ayuda} />}
      </dt>
      <dd className={`tabular-nums ${fuerte ? 'font-semibold' : ''}`}>
        {typeof valor === 'number' ? numero(valor) : valor}
      </dd>
    </div>
  );
}
